import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { useSite } from '@/components/generals/site-context'

import { siteConfig } from '@/constants/site'

const serializeJsonLd = (value: Record<string, unknown>): string =>
    JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    path,
    noIndex,
    jsonLd
}): ReactNode => {
    const { origin } = useSite()
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const base = origin || siteConfig.url
    const url = base ? `${base}${path ?? ''}` : ''

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:site_name' content={siteConfig.name} />
            {url && <meta property='og:url' content={url} />}
            {url && path !== undefined && !noIndex && <link rel='canonical' href={url} />}
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />

            {noIndex && <meta name='robots' content='noindex, nofollow' />}
            {ogImage && <meta property='og:image' content={ogImage} />}
            {jsonLd && (
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: serializeJsonLd(jsonLd) }}
                />
            )}
        </Head>
    )
}
